import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Dialog } from 'primereact/dialog';
import { Button } from '../../../../../components/ui/Button';
import { Dropdown } from 'primereact/dropdown';

import "../../../css/layouts.css";
import '../../../../css/designTools.css';

import DynamicContainer from './Controls/DynamicContainer';
import AreaControls from "./Controls/AreaControls";
import LayoutNameController from './Controls/LayoutNameController';
import DesignToolsPanel from './Controls/DesignToolsPanel';
import ToolTutorialModal from './Controls/ToolTutorialModal';
import NameValidationModal from './Modals/NameValidationModal';

import { toggleDialog } from '../../../../../redux/Slices/Global/globalSlice';
import { setUnit } from '../../../../../redux/Slices/layoutSlice';
import { layoutColor, defaultArea, unitOptions } from '../../../../../config/layoutConstants';
import { convertToPx, handleSwitchUnit } from '../../../../../config/layoutHelper';
import { useCreateTemplate } from '../../../../../queries/layouts';
import useDesignTools from '../../../../../hooks/useDesignTools';

function CustomLayoutPopUp() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const openDialog = useSelector((state) => state.global.openDialog);
    const unit = useSelector((state) => state.layout.unit);

    // Form Data
    const [layoutName, setLayoutName] = useState({ isEditing: true, name: "" });
    const [titleError, setTitleError] = useState("");
    const [areas, setAreas] = useState([defaultArea]);
    const [currentAspectRatio, setCurrentAspectRatio] = useState({});
    
    // UI State
    const [isMaximized, setIsMaximized] = useState(true);
    const [sidebarWidth, setSidebarWidth] = useState(384);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [isCanvasFullscreen, setIsCanvasFullscreen] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [sidebarTab, setSidebarTab] = useState('areas'); // 'areas' or 'tools'
    const [isSaveDisabled, setIsSaveDisabled] = useState(false);
    const [overlappingAreas, setOverlappingAreas] = useState([]);
    const [showPreview, setShowPreview] = useState(false);
    const [showNameModal, setShowNameModal] = useState(false);
    const scrollableDivRef = useRef(null);

    // Design Tools Hook
    const {
        toolsState,
        setActiveTab,
        updateTool,
        toggleTool,
        setAreaColor,
        setAreaOpacity,
        showToolTutorial,
        hideTutorial,
        selectedTool,
        showTutorial,
        toggleSidebarPin,
        toggleDarkMode
    } = useDesignTools();

    const isSidebarPinned = toolsState?.sidebarPinned;
    const isDarkMode = toolsState?.darkMode;
    const [isSidebarOpenUnpinned, setIsSidebarOpenUnpinned] = useState(false);

    // Mutation
    const { mutate: createTemplateMutation, isLoading } = useCreateTemplate();

    // Effect: Handle Unit Switching
    useEffect(() => {
        if (unit.value === "px") {
            const areasConverted = convertToPx(areas, currentAspectRatio);
            setAreas(areasConverted);
        }
        if (unit.value === "%") {
            const areasConverted = handleSwitchUnit(areas, currentAspectRatio);
            setAreas(areasConverted);
        }
    }, [unit, currentAspectRatio]);

    // Close sidebar logic
    useEffect(() => {
        if (!isSidebarPinned) {
            const handleClickOutside = (e) => {
                const sidebar = document.getElementById('create-layout-sidebar');
                const strip = document.getElementById('create-sidebar-strip');
                const header = document.querySelector('.create-layout-header'); 

                if (isSidebarOpenUnpinned && 
                    sidebar && !sidebar.contains(e.target) && 
                    strip && !strip.contains(e.target) && 
                    header && !header.contains(e.target)) {
                    setIsSidebarOpenUnpinned(false);
                }
            };
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        } else {
            setIsSidebarOpenUnpinned(false); 
        }
    }, [isSidebarPinned, isSidebarOpenUnpinned]);

    const executeSave = (nameToUse) => {
        // Ensure percent unit before sending
        let areasToSend = areas;
        if (unit.value === "px") {
             areasToSend = handleSwitchUnit(areas, currentAspectRatio);
        }

        const orderedAreas = areasToSend.map((area, index) => ({ 
            ...area, 
            order: index + 1,
        }));
        
        createTemplateMutation({ layoutName: { name: nameToUse, isEditing: false }, areas: orderedAreas, currentAspectRatio });
    };

    const handleCreateTemplate = () => {
        const currentName = layoutName.name.trim();
        if (currentName) {
             if(layoutName.isEditing) setLayoutName(prev => ({ ...prev, isEditing: false }));
             executeSave(currentName);
        } else {
             setShowNameModal(true);
        }
    };

    const onModalSave = (newName) => {
        setLayoutName({ name: newName, isEditing: false });
        setShowNameModal(false);
        setTitleError("");
        executeSave(newName);
    };

    const handleUnitChange = (unitValue) => {
        dispatch(setUnit({ value: unitValue }));
    };

    const handleSidebarTabClick = (tab) => {
        setSidebarTab(tab);
        if (!isSidebarPinned) {
            setIsSidebarOpenUnpinned(true);
        }
    };

    // Zoom Logic
    const handleZoomIn = () => setZoomLevel(prev => Math.min(prev + 0.1, 2));
    const handleZoomOut = () => setZoomLevel(prev => Math.max(prev - 0.1, 0.5));
    const handleResetZoom = () => setZoomLevel(1);
    const toggleCanvasFullscreen = () => setIsCanvasFullscreen(prev => !prev);

    // Resizing Logic
    const startResizing = useCallback((mouseDownEvent) => {
        mouseDownEvent.preventDefault();
        mouseDownEvent.stopPropagation();
        setIsResizing(true);
        const startX = mouseDownEvent.clientX;
        const startWidth = sidebarWidth;

        const doDrag = (mouseMoveEvent) => {
             const newWidth = startWidth - (mouseMoveEvent.clientX - startX);
             if (newWidth > 280 && newWidth < 600) {
                 setSidebarWidth(newWidth);
             }
        };

        const stopDrag = () => {
             setIsResizing(false);
             document.removeEventListener('mousemove', doDrag);
             document.removeEventListener('mouseup', stopDrag);
        };

        document.addEventListener('mousemove', doDrag);
        document.addEventListener('mouseup', stopDrag);
    }, [sidebarWidth]);

    return (
        <Dialog 
            visible={openDialog.CustomLayoutPopUp}
            breakpoints={{ '960px': '95vw' }}
            showHeader={false} // Custom Header
            style={{ overflow: 'hidden' }}
            modal
            blockScroll={true}
            className={`p-fluid w-full ${isMaximized ? 'p-dialog-maximized' : 'p-dialog-default md:w-[100%] lg:w-[75%] h-[85%]'}`}
            onHide={() => dispatch(toggleDialog("CustomLayoutPopUp"))}
            contentClassName="p-0 !overflow-hidden" // Remove default padding/overflow
        >
            <div className={`flex flex-col h-full overflow-hidden font-inter transition-colors duration-300 ${isDarkMode ? 'bg-slate-950 text-gray-100' : 'bg-gray-50 text-gray-900'}`}>
                {/* Custom Studio Header */}
                <div className={`create-layout-header h-14 border-b px-4 flex items-center justify-between shrink-0 z-40 sticky top-0 shadow-sm relative transition-colors duration-300 ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`}>
                    
                    {/* Left: Branding */}
                    <div className="flex items-center gap-3 flex-1 w-1/3">
                         <div className={`flex items-center gap-2 group cursor-default ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            <div className={`w-6 h-6 rounded flex items-center justify-center text-white shadow-sm ${isDarkMode ? 'bg-indigo-500' : 'bg-gray-900'}`}>
                                <i className="bx bxs-layout text-xs"></i>
                            </div>
                            <span className="text-sm font-semibold tracking-wide">{t('layouts.designer_create_new_layout')}</span>
                         </div>
                    </div>

                    {/* Center: Title Input */}
                    <div className="flex-1 shrink-0 flex justify-center w-1/3">
                          <div className="w-full max-w-xs">
                               <LayoutNameController 
                                    layoutName={layoutName} 
                                    setLayoutName={setLayoutName} 
                                    titleError={titleError} 
                               />
                          </div>
                    </div>

                    {/* Right: Tools & Actions */}
                    <div className="flex items-center justify-end gap-3 flex-1 w-1/3">
                        {/* Unit Toggle */}
                        <div className="flex items-center gap-2">
                             <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('layouts.designer_unit')}</span>
                             <div className="flex bg-gray-100 p-0.5 rounded-lg border border-gray-200 h-8">
                                 {unitOptions.map((opt) => (
                                     <button
                                        key={opt.value}
                                        onClick={() => handleUnitChange(opt.value)}
                                        className={`px-3 text-xs font-medium rounded-md transition-all ${
                                            (unit?.value || unitOptions[0].value) === opt.value 
                                            ? "bg-white text-gray-900 shadow-sm border border-gray-200" 
                                            : "text-gray-500 hover:text-gray-700"
                                        }`}
                                     >
                                         {opt.label}
                                     </button>
                                 ))}
                             </div>
                         </div>
                         
                         <div className={`h-4 w-px mx-1 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-300'}`}></div>

                        {/* Dark Mode */}
                        <button
                            onClick={toggleDarkMode}
                            className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all duration-200 border ${
                                isDarkMode 
                                ? "bg-slate-800 text-yellow-400 border-slate-700 hover:bg-slate-700 hover:text-yellow-300" 
                                : "bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200 hover:text-gray-700"
                            }`}
                            title={isDarkMode ? t('layouts.designer_switch_light_mode') : t('layouts.designer_switch_dark_mode')}
                        >
                            <i className={`bx ${isDarkMode ? 'bx-sun' : 'bx-moon'} text-lg`}></i>
                        </button>

                        <div className={`h-4 w-px mx-1 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-300'}`}></div>

                        {/* Pin Sidebar */}
                        <button 
                            onClick={toggleSidebarPin}
                            className={`h-8 flex items-center gap-2 px-3 rounded-lg transition-all duration-200 border text-xs font-semibold ${
                                isSidebarPinned 
                                ? "bg-indigo-50 text-indigo-600 border-indigo-100 hover:bg-indigo-100" 
                                : "text-gray-500 hover:text-gray-700 hover:bg-gray-100 border-transparent"
                            }`}
                            title={isSidebarPinned ? t('layouts.designer_unpin_sidebar') : t('layouts.designer_pin_sidebar')}
                        >
                            <i className={`bx ${isSidebarPinned ? 'bx-pin' : 'bx-pin'} text-sm`}></i>
                            <span>{isSidebarPinned ? t('layouts.designer_pinned') : t('layouts.designer_pin')}</span>
                        </button>

                         {/* Preview Button */}
                         <button 
                            onClick={() => setShowPreview(!showPreview)}
                            className={`h-8 flex items-center gap-2 px-3 rounded-lg font-semibold text-xs shadow-lg transition-all duration-300 border ${
                                showPreview 
                                ? "bg-green-600 text-white border-green-500 shadow-green-500/30" 
                                : "bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-indigo-500/30 hover:scale-105 border-indigo-500/50"
                            }`}
                            title={t('layouts.designer_preview_layout')}
                        >
                            <i className={`bx ${showPreview ? 'bx-pause' : 'bx-show'} text-sm`}></i>
                            <span>{showPreview ? t('layouts.designer_stop_preview') : t('layouts.designer_preview')}</span>
                        </button>

                        <div className={`h-4 w-px mx-1 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-300'}`}></div>

                        {/* Close Button */}
                        <button 
                            onClick={() => dispatch(toggleDialog("CustomLayoutPopUp"))}
                            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 transition-all duration-200 border border-transparent hover:border-red-100"
                            title={t('layouts.designer_close')}
                        >
                            <i className="bx bx-x text-xl"></i>
                        </button>
                    </div>
                </div>

                {/* Main Workspace */}
                <div className="flex flex-1 overflow-hidden relative">
                    {/* Unpinned Sidebar Strip */}
                    {!isSidebarPinned && (
                        <div id="create-sidebar-strip" className={`absolute right-0 top-0 bottom-0 w-12 border-l z-30 flex flex-col items-center py-4 gap-4 shadow-sm transition-colors duration-300 ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`}>
                            <button 
                                onClick={() => handleSidebarTabClick('areas')}
                                className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                                    (isSidebarOpenUnpinned && sidebarTab === 'areas') 
                                    ? 'bg-indigo-50 text-indigo-600' 
                                    : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                                }`}
                                title={t('layouts.designer_areas')}
                            >
                                <i className="bx bx-grid-alt text-xl"></i>
                            </button>
                            <div className={`w-8 h-px my-1 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-200'}`}></div>
                            
                            {[
                                { id: 'view', label: t('layouts.designer_tab_view'), icon: 'bx-show' },
                                { id: 'grid', label: t('layouts.designer_tab_grid'), icon: 'bx-grid-alt' },
                                { id: 'measure', label: t('layouts.designer_tab_measure'), icon: 'bx-ruler' },
                                { id: 'style', label: t('layouts.designer_tab_borders'), icon: 'bx-border-all' },
                                { id: 'colors', label: t('layouts.designer_tab_colors'), icon: 'bx-palette' }
                            ].map(tool => (
                                <button 
                                    key={tool.id}
                                    onClick={() => {
                                        setActiveTab(tool.id);
                                        handleSidebarTabClick('tools');
                                    }}
                                    className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                                        (isSidebarOpenUnpinned && sidebarTab === 'tools' && toolsState?.activeTab === tool.id) 
                                        ? 'bg-purple-500/20 text-purple-400' 
                                        : (isDarkMode ? 'text-slate-400 hover:bg-slate-800 hover:text-slate-200' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700')
                                    }`}
                                    title={tool.label}
                                >
                                    <i className={`bx ${tool.icon} text-xl`}></i>
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Canvas Area */}
                    <div 
                        className={`flex-1 overflow-hidden relative flex flex-col transition-all ease-out ${isResizing ? '' : 'duration-300'} ${isDarkMode ? 'bg-slate-950' : 'bg-gray-50/50'}`}
                        style={{
                            paddingRight: !isSidebarPinned 
                                ? (isSidebarOpenUnpinned ? `${sidebarWidth + 48}px` : '3rem') 
                                : 0
                        }}
                    > 
                        <div 
                            className={`flex-1 overflow-auto flex items-center justify-center custom-scrollbar relative transition-all duration-300 ${isCanvasFullscreen ? 'p-0' : 'p-8'}`} 
                            ref={scrollableDivRef}
                        >
                            {/* Background Pattern */}
                            <div className="absolute inset-0 z-0 opacity-[0.4] pointer-events-none" style={{ backgroundImage: 'radial-gradient(#94a3b8 1px, transparent 1px)', backgroundSize: '24px 24px' }}></div>
                            
                            {/* Dynamic Container */}
                            <div className="relative z-10 w-full h-full flex items-center justify-center transition-transform duration-200 ease-out origin-center">
                                <DynamicContainer
                                    isMaximized={isMaximized}
                                    isCanvasFullscreen={isCanvasFullscreen}
                                    areas={areas}
                                    setAreas={setAreas}
                                    layoutColor={layoutColor}
                                    currentAspectRatio={currentAspectRatio}
                                    setCurrentAspectRatio={setCurrentAspectRatio}
                                    scrollableDivRef={scrollableDivRef}
                                    zoomLevel={zoomLevel}
                                    toolsState={toolsState}
                                    isPaused={showPreview}
                                />
                            </div>
                        </div>

                        {/* Floating Zoom Controls */}
                        <div className={`absolute bottom-6 left-6 flex flex-col gap-2 z-30 p-1.5 rounded-xl shadow-lg border transition-colors duration-300 ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-100'}`}>
                              <button onClick={toggleCanvasFullscreen} className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${isDarkMode ? 'hover:bg-slate-700 text-gray-400' : 'hover:bg-gray-100 text-gray-600'} ${isCanvasFullscreen ? 'text-indigo-600' : ''}`} title={t('layouts.designer_fullscreen')}>
                                  <i className={`bx ${isCanvasFullscreen ? 'bx-exit-fullscreen' : 'bx-fullscreen'} text-xl`}></i>
                              </button>
                              <div className={`h-px ${isDarkMode ? 'bg-slate-700' : 'bg-gray-200'}`}></div>
                              <button onClick={handleZoomIn} className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${isDarkMode ? 'hover:bg-slate-700 text-gray-400' : 'hover:bg-gray-100 text-gray-600'}`} title={t('layouts.designer_zoom_in')}><i className="bx bx-plus text-xl"></i></button>
                              <button onClick={handleResetZoom} className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors font-bold text-xs ${isDarkMode ? 'hover:bg-slate-700 text-gray-400' : 'hover:bg-gray-100 text-gray-600'}`}>{Math.round(zoomLevel * 100)}%</button>
                              <button onClick={handleZoomOut} className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${isDarkMode ? 'hover:bg-slate-700 text-gray-400' : 'hover:bg-gray-100 text-gray-600'}`} title={t('layouts.designer_zoom_out')}><i className="bx bx-minus text-xl"></i></button>
                        </div>
                    </div>

                    {/* Resizer Handle */}
                    {(isSidebarPinned || isSidebarOpenUnpinned) && (
                         <div
                            className={`w-1 hover:w-1.5 bg-gray-200 hover:bg-indigo-400 cursor-ew-resize transition-all z-50 flex items-center justify-center group shrink-0 ${isResizing ? 'bg-indigo-500 w-1.5' : ''} ${!isSidebarPinned ? 'absolute top-0 bottom-0' : 'relative'}`}
                            style={{ right: !isSidebarPinned ? `${sidebarWidth + 48}px` : 'auto' }}
                            onMouseDown={startResizing}
                         >
                            <div className={`h-8 w-1 rounded-full group-hover:bg-white transition-colors absolute ${isResizing ? 'bg-white' : 'bg-gray-400'}`} />
                         </div>
                    )}

                    {/* Sidebar */}
                    <div 
                        id="create-layout-sidebar"
                        className={`border-l shadow-xl z-40 flex flex-col h-full transition-all ease-out ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'} ${!isSidebarPinned ? 'absolute right-12 top-0 bottom-0 shadow-2xl border-l-0' : 'relative'} ${isResizing ? '' : 'duration-300'}`}
                        style={{ 
                            width: `${sidebarWidth}px`,
                            transform: !isSidebarPinned && !isSidebarOpenUnpinned ? 'translateX(100%)' : 'translateX(0)',
                            opacity: !isSidebarPinned && !isSidebarOpenUnpinned ? 0 : 1,
                            visibility: !isSidebarPinned && !isSidebarOpenUnpinned ? 'hidden' : 'visible'
                        }}
                    >
                          {/* Sidebar Header */}
                          <div className={`p-4 border-b sticky top-0 z-10 backdrop-blur-md shrink-0 flex items-center justify-between transition-colors ${isDarkMode ? 'bg-slate-900/95 border-slate-800' : 'bg-white/95 border-gray-100'}`}>
                               <h2 className={`font-semibold flex items-center gap-2 text-sm uppercase tracking-wide ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                   <i className={`bx ${sidebarTab === 'areas' ? 'bx-grid-alt text-indigo-500' : 'bx-wrench text-purple-500'} text-lg`}></i> 
                                   {sidebarTab === 'areas' ? t('layouts.designer_area_manager') : t('layouts.designer_design_tools')}
                               </h2>
                               {!isSidebarPinned && (
                                   <button onClick={() => setIsSidebarOpenUnpinned(false)} className="text-gray-400 hover:text-gray-600"><i className="bx bx-x text-xl"></i></button>
                               )}
                          </div>

                          {/* Sidebar Tabs (Pinned Only) */}
                          {isSidebarPinned && (
                            <div className="px-4 pb-2">
                                <div className={`flex gap-2 p-1 rounded-lg border transition-colors ${isDarkMode ? 'bg-slate-950 border-slate-800' : 'bg-gray-50 border-gray-200'}`}>
                                    <button onClick={() => setSidebarTab('areas')} className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md transition-all text-xs font-semibold ${sidebarTab === 'areas' ? (isDarkMode ? 'bg-slate-800 text-indigo-400 shadow-sm border border-slate-700' : 'bg-white text-indigo-600 shadow-sm border border-gray-200') : (isDarkMode ? 'text-slate-500 hover:text-slate-300 hover:bg-slate-900' : 'text-gray-600 hover:bg-white/50')}`}>
                                        <i className="bx bx-grid-alt text-sm"></i><span>{t('layouts.designer_areas')}</span>
                                    </button>
                                    <button onClick={() => setSidebarTab('tools')} className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md transition-all text-xs font-semibold ${sidebarTab === 'tools' ? (isDarkMode ? 'bg-slate-800 text-purple-400 shadow-sm border border-slate-700' : 'bg-white text-purple-600 shadow-sm border border-gray-200') : (isDarkMode ? 'text-slate-500 hover:text-slate-300 hover:bg-slate-900' : 'text-gray-600 hover:bg-white/50')}`}>
                                        <i className="bx bx-wrench text-sm"></i><span>{t('layouts.designer_tools')}</span>
                                    </button>
                                </div>
                            </div>
                          )}

                          {/* Sidebar Content */}
                          <div className={`flex-1 overflow-y-auto p-4 custom-scrollbar ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
                               {sidebarTab === 'areas' ? (
                                   <>
                                       <AreaControls
                                           layoutColor={layoutColor}
                                           setIsConflictScreens={() => {}} // Not needed for create
                                           areas={areas}
                                           setAreas={setAreas}
                                           currentAspectRatio={currentAspectRatio}
                                           isSaveDisabled={isSaveDisabled}
                                           overlappingAreas={overlappingAreas}
                                           setOverlappingAreas={setOverlappingAreas}
                                           setIsSaveDisabled={setIsSaveDisabled}
                                           scrollableDivRef={scrollableDivRef}
                                           isDarkMode={isDarkMode}
                                           sidebarWidth={sidebarWidth}
                                           isCreateMode={true} // Flag for specific create-mode logic if needed
                                       />
                                       
                                   </>
                               ) : (
                                   <DesignToolsPanel
                                       isOpen={true}
                                       onClose={() => setSidebarTab('areas')}
                                       toolsState={toolsState}
                                       updateTool={updateTool}
                                       toggleTool={toggleTool}
                                       setAreaColor={setAreaColor}
                                       setAreaOpacity={setAreaOpacity}
                                       showToolTutorial={showToolTutorial}
                                       areas={areas}
                                       embedded={true}
                                       activeTab={toolsState?.activeTab || 'view'}
                                       setActiveTab={setActiveTab}
                                   />
                               )}
                          </div>

                          {/* Sidebar Footer */}
                          <div className={`p-4 border-t flex gap-3 sticky bottom-0 z-10 shrink-0 transition-colors ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-gray-50 border-gray-200'}`}>
                               <Button
                                    label={t('buttons.cancel')}
                                    className="flex-1 py-3 bg-white text-gray-600 border border-gray-300 hover:bg-gray-50 hover:text-gray-800 rounded-lg shadow-sm transition-all text-sm font-semibold"
                                    onClick={() => dispatch(toggleDialog("CustomLayoutPopUp"))}
                               />
                               <Button
                                    loading={isLoading}
                                    label={t('layouts.designer_create_layout')}
                                    icon="bx bx-plus"
                                    className="flex-1 py-3 bg-indigo-600 border-indigo-600 hover:bg-indigo-700 shadow-md shadow-indigo-200 rounded-lg text-white transition-all text-sm font-bold tracking-wide" 
                                    disabled={isSaveDisabled} 
                                    onClick={() => handleCreateTemplate()} 
                               />
                          </div>
                    </div>
                </div>

                {/* Name Validation Modal */}
                <NameValidationModal
                    visible={showNameModal}
                    onHide={() => setShowNameModal(false)}
                    onSave={onModalSave}
                    isDarkMode={isDarkMode}
                />

                {/* Tutorial Modal */}
                <ToolTutorialModal
                    visible={showTutorial}
                    onHide={hideTutorial}
                    toolName={selectedTool}
                />
            </div>
        </Dialog>
    );
}

export default CustomLayoutPopUp;